import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

export type MeetingExchangeResource = {
  _id: string
  firstname: string
  lastname: string
  picture?: string
}

export type MeetingExchange = {
  _id: string
  fromId: MeetingExchangeResource
  toId: MeetingExchangeResource
  fromMeetingId: any
  toMeetingId: any
  fromMeetingStart: Date
  fromMeetingEnd: Date
  toMeetingStart: Date
  toMeetingEnd: Date
  accepted: boolean | null
  isClosed: boolean
  isClosedBySender: boolean
  fromMeetingDeleted: boolean
  toMeetingDeleted: boolean
  comment?: string
  _created: Date
  _firmId: string
}

export type MeetingExchangePayload = {
  fromId: string
  officeId: string
  countryId: string
  fromMeetingId: string
  fromMeetingStart: Date
  fromMeetingEnd: Date
  toMeetingId: string
  toMeetingStart: Date
  toMeetingEnd: Date
  toId: string
  _firmId: string
}

export type ExchangeGroup = MeetingExchange & {
  exchanges: MeetingExchange[]
}

const afterReceivingExchange = (data: any) => {
  if (!data) return data
  const transform = (exchange: any) => ({
    ...exchange,
    fromMeetingStart: exchange.fromMeetingStart ? new Date(exchange.fromMeetingStart) : exchange.fromMeetingStart,
    fromMeetingEnd: exchange.fromMeetingEnd ? new Date(exchange.fromMeetingEnd) : exchange.fromMeetingEnd,
    toMeetingStart: exchange.toMeetingStart ? new Date(exchange.toMeetingStart) : exchange.toMeetingStart,
    toMeetingEnd: exchange.toMeetingEnd ? new Date(exchange.toMeetingEnd) : exchange.toMeetingEnd,
    _created: exchange._created ? new Date(exchange._created) : exchange._created,
  })
  if (Array.isArray(data)) return data.map(transform)
  return transform(data)
}

const getByMeetingId = (axios: IFactoryFunctionRequest) => (meetingId: string) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/meeting/${meetingId}`,
    afterReceivingHook: afterReceivingExchange,
  })

const create = (axios: IFactoryFunctionRequest) => (payload: MeetingExchangePayload) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2`,
    data: payload,
    afterReceivingHook: afterReceivingExchange,
  })

const getFreeMeetings = (axios: IFactoryFunctionRequest) => (firmId: string) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/recruitmentProcess/freeMeetings/${firmId}`,
  })

const getMeetingsInterval = (axios: IFactoryFunctionRequest) => (firmId: string, interval: { dateStart: Date; dateEnd: Date }) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetings/interval/${firmId}`,
    data: interval,
  })

const getSentExchanges = (axios: IFactoryFunctionRequest) => () =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/sent`,
    afterReceivingHook: afterReceivingExchange,
  })

const getReceivedExchanges = (axios: IFactoryFunctionRequest) => () =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/received`,
    afterReceivingHook: afterReceivingExchange,
  })

const acceptExchange = (axios: IFactoryFunctionRequest) => (data: { exchangeId: string; fromMeetingProcess?: string | null; toMeetingProcess?: string | null }) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/acceptExchange`,
    data,
  })

const updateExchange = (axios: IFactoryFunctionRequest) => (id: string, data: { accepted: boolean; comment?: string }) =>
  axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/${id}`,
    data,
  })

const closeExchange = (axios: IFactoryFunctionRequest) => (id: string) =>
  axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/${id}`,
  })

const sendReminderExchange = (axios: IFactoryFunctionRequest) => (id: string) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/sendReminder/${id}`,
  })

const getAllExchanges = (axios: IFactoryFunctionRequest) => (firmId: string) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/all/${firmId}`,
    afterReceivingHook: afterReceivingExchange,
  })

export default (axios: AxiosInstance) => {
  const factory = useAxios(axios)
  return {
    getByMeetingId: getByMeetingId(factory),
    create: create(factory),
    getFreeMeetings: getFreeMeetings(factory),
    getMeetingsInterval: getMeetingsInterval(factory),
    getSentExchanges: getSentExchanges(factory),
    getReceivedExchanges: getReceivedExchanges(factory),
    acceptExchange: acceptExchange(factory),
    updateExchange: updateExchange(factory),
    closeExchange: closeExchange(factory),
    sendReminderExchange: sendReminderExchange(factory),
    getAllExchanges: getAllExchanges(factory),
  }
}
